import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { baseURL } from '../config';
import AuthContext from '../Context/AuthContext';
import Searchbar from '../Components/Searchbar';
import AssetDropzone, { ASSET_TYPES } from '../Components/AssetDropzone';
import LoadingAnimation from '../Components/LoadingAnimation';
import PaginationComponent from '../Components/PaginationComponent';
import Popup from '../Components/Popup';
import ButtonSolid from '../Components/ButtonSolid';
import ButtonOutline from '../Components/ButtonOutline';
import { CloseIcon, DeleteIcon, EyeIcon, TemplateFolder } from '../Components/Icons';
import { STAGE_LABEL, uploadNewAsset } from '../utils/assetUpload';

function AssetManagerListView() {
    const { token, logout } = useContext(AuthContext);
    const [currentFolder, setCurrentFolder] = useState(null); // null = folder list; else one of ASSET_TYPES
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [assetData, setAssetData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalAssets, setTotalAssets] = useState(0);
    const [fetchingAssetData, setFetchingAssetData] = useState(false);
    const [reloadFlag, setReloadFlag] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [pendingUpload, setPendingUpload] = useState(null); // { file, type }
    const [lastChosenType, setLastChosenType] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStage, setUploadStage] = useState(null); // 'checking' | 'uploading' | 'finalizing'
    const uploadAbortRef = useRef(null);
    const [assetPendingDelete, setAssetPendingDelete] = useState(null);
    const [usageAssemblies, setUsageAssemblies] = useState([]);
    const [fetchingUsage, setFetchingUsage] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const debounceRef = useRef(null);

    const isBrowsing = currentFolder !== null || search.trim() !== '';

    useEffect(() => {
        if (!isBrowsing) {
            setAssetData([]);
            setTotalPages(1);
            setTotalAssets(0);
            return;
        }
        const fetchAssetData = async () => {
            try {
                setFetchingAssetData(true);
                const response = await axios.get(`${baseURL}/asset/getAllAssets`, {
                    headers: { 'ngrok-skip-browser-warning': 'any', 'x-access-token': token },
                    params: { page, limit, search, filter: currentFolder || 'all' },
                });
                setAssetData(response.data.data.assetData || []);
                setTotalPages(response.data.data.pagination.totalPages || 1);
                setTotalAssets(response.data.data.pagination.totalAssets);
            } catch (error) {
                if (error.response?.status === 401) logout();
            } finally {
                setFetchingAssetData(false);
            }
        };
        fetchAssetData();
    }, [isBrowsing, currentFolder, search, page, limit, token, logout, reloadFlag]);

    const openFolder = (type) => {
        setCurrentFolder(type);
        setPage(1);
        setSearchInput('');
        setSearch('');
    };

    const backToFolders = () => {
        setCurrentFolder(null);
        setPage(1);
        setSearchInput('');
        setSearch('');
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(value);
            setPage(1);
        }, 500);
    };

    const closeUploadModal = () => {
        if (uploading) return;
        setShowUploadModal(false);
        setPendingUpload(null);
        setLastChosenType(null);
        setUploadProgress(0);
        setUploadStage(null);
    };

    const cancelUpload = () => {
        uploadAbortRef.current?.abort();
    };

    const submitPendingUpload = async () => {
        if (!pendingUpload.type) {
            toast.error('Please select an asset type');
            return;
        }
        const controller = new AbortController();
        uploadAbortRef.current = controller;
        try {
            setUploading(true);
            setUploadProgress(0);
            const result = await uploadNewAsset({
                file: pendingUpload.file,
                type: pendingUpload.type,
                token,
                signal: controller.signal,
                onStage: setUploadStage,
                onProgress: setUploadProgress,
            });
            toast.success(result.duplicate ? 'An identical file already exists — reusing it' : 'Asset uploaded successfully');
            setShowUploadModal(false);
            setPendingUpload(null);
            setLastChosenType(null);
            setReloadFlag((prev) => !prev);
        } catch (error) {
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                toast.error('Upload cancelled');
            } else if (error.response?.status === 401) {
                logout();
            } else {
                toast.error(error.response?.data?.message || error.message || 'Error uploading asset');
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setUploadStage(null);
            uploadAbortRef.current = null;
        }
    };

    const openDeleteConfirm = async (asset) => {
        setAssetPendingDelete(asset);
        setUsageAssemblies([]);
        try {
            setFetchingUsage(true);
            const response = await axios.get(`${baseURL}/asset/${asset._id}/usage`, {
                headers: { 'x-access-token': token },
            });
            setUsageAssemblies(response.data.data || []);
        } catch (error) {
            if (error.response?.status === 401) logout();
        } finally {
            setFetchingUsage(false);
        }
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);
            await axios.delete(`${baseURL}/asset/${assetPendingDelete._id}`, {
                headers: { 'x-access-token': token },
            });
            toast.success('Asset deleted successfully');
            setAssetPendingDelete(null);
            setReloadFlag((prev) => !prev);
        } catch (error) {
            if (error.response?.status === 401) {
                logout();
            } else {
                toast.error('Error deleting asset');
            }
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className='py-3 md:py-6'>
            <div className='flex justify-between items-center pb-5'>
                <p className='text-lg font-medium md:text-xl md:font-semibold'>Asset Manager</p>
                <ButtonSolid buttonText='+ Add' onClick={() => setShowUploadModal(true)} />
            </div>

            <div className='p-3 bg-white shadow-md rounded-lg min-h-80'>
                <div className='flex justify-between flex-wrap px-1 md:pt-3 md:pb-6 items-center gap-3'>
                    <div className='pr-2 whitespace-nowrap text-xl font-medium'>
                        {currentFolder ? (
                            <span>
                                <span onClick={backToFolders} className='text-blue-500 cursor-pointer'>All Assets</span>
                                {' / '}{currentFolder}
                            </span>
                        ) : search.trim() ? (
                            <span>
                                <span onClick={backToFolders} className='text-blue-500 cursor-pointer'>All Assets</span>
                                {' / '}Search results
                            </span>
                        ) : (
                            'All Assets'
                        )}
                    </div>
                    <Searchbar value={searchInput} onChange={(e) => handleSearchChange(e.target.value)} />
                </div>

                {!isBrowsing ? (
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-4 p-2'>
                        {ASSET_TYPES.map((type) => {
                            return (
                                <div
                                    key={type}
                                    onClick={() => openFolder(type)}
                                    className='cursor-pointer border border-slate-100 bg-slate-50 hover:bg-slate-200 rounded-md p-4 flex flex-col items-center gap-2 text-center'
                                >
                                    <TemplateFolder />
                                    <p className='font-medium text-sm md:text-base'>{type}</p>
                                </div>
                            );
                        })}
                    </div>
                ) : fetchingAssetData ? (
                    <LoadingAnimation />
                ) : assetData.length === 0 ? (
                    <p className='p-4'>{`No assets found for '${search}'`}</p>
                ) : (
                    <div className='mt-2 border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100 bg-white'>
                        {assetData.map((asset) => (
                            <div
                                key={asset._id}
                                className='flex items-center justify-between gap-3 md:gap-4 px-3 md:px-4 py-3 hover:bg-slate-50'
                            >
                                <div className='min-w-0'>
                                    <p className='break-words text-sm md:text-base text-gray-700 select-text'>{asset.name}</p>
                                    {!currentFolder && <p className='text-xs md:text-sm text-gray-500'>{asset.type}</p>}
                                </div>
                                <div className='flex items-center shrink-0 gap-3 md:gap-4'>
                                    <Link
                                        target='blank'
                                        to={asset.source}
                                        title='View Asset'
                                        aria-label='View Asset'
                                        className='p-1.5 rounded-md text-orange-500 hover:bg-orange-50 hover:text-orange-600'
                                    >
                                        <EyeIcon size='w-5 h-5' />
                                    </Link>
                                    <button
                                        type='button'
                                        onClick={() => openDeleteConfirm(asset)}
                                        title='Delete Asset'
                                        aria-label='Delete Asset'
                                        className='p-1.5 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700'
                                    >
                                        <DeleteIcon size='w-5 h-5' />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {isBrowsing && (
                    <PaginationComponent
                        totalData={totalAssets}
                        totalPages={totalPages}
                        currentPage={page}
                        limitValue={limit}
                        setLimitValue={setLimit}
                        setCurrentPage={setPage}
                        siblings={2}
                    />
                )}
            </div>

            {showUploadModal && (
                <Popup maxWidth='md:max-w-[500px]'>
                    <div className='p-2'>
                        <div className='flex justify-between items-center mb-4'>
                            <p className='text-lg font-semibold text-gray-700'>Add Asset</p>
                            <button
                                type='button'
                                disabled={uploading}
                                onClick={closeUploadModal}
                                className='p-2 border-2 bg-white border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed'
                            >
                                <CloseIcon size='w-5 h-5' />
                            </button>
                        </div>
                        {!pendingUpload ? (
                            <AssetDropzone
                                disabled={uploading}
                                defaultType={lastChosenType || currentFolder}
                                onFileSelected={(file, type) => { setLastChosenType(type); setPendingUpload({ file, type }); }}
                            />
                        ) : uploading ? (
                            <div>
                                <p className='text-sm text-gray-600 mb-2'>
                                    {STAGE_LABEL[uploadStage] || 'Working...'} <span className='font-medium'>{pendingUpload.file.name}</span>
                                </p>
                                {uploadStage === 'uploading' && (
                                    <>
                                        <div className='w-full bg-gray-200 rounded-full h-2 mb-2'>
                                            <div className='bg-orange-400 h-2 rounded-full transition-all' style={{ width: `${uploadProgress}%` }} />
                                        </div>
                                        <p className='text-xs text-gray-400 mb-4'>{uploadProgress}%</p>
                                    </>
                                )}
                                {uploadStage !== 'uploading' && <div className='mb-4' />}
                                <ButtonOutline buttonText='Cancel Upload' onClick={cancelUpload} className='w-full' />
                            </div>
                        ) : (
                            <div>
                                <p className='text-sm text-gray-600 mb-4'>
                                    Upload <span className='font-medium'>{pendingUpload.file.name}</span> as{' '}
                                    <span className='font-medium'>{pendingUpload.type || '(no type selected)'}</span>?
                                </p>
                                <div className='flex flex-col md:flex-row-reverse gap-4'>
                                    <ButtonSolid
                                        buttonText='Upload'
                                        onClick={submitPendingUpload}
                                        className='w-full'
                                    />
                                    <ButtonOutline
                                        buttonText='Choose Different File'
                                        onClick={() => setPendingUpload(null)}
                                        className='w-full'
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Popup>
            )}

            {assetPendingDelete && (
                <Popup>
                    <div className='p-4'>
                        <p className='text-lg font-semibold text-gray-700 mb-2'>Delete "{assetPendingDelete.name}"?</p>
                        {fetchingUsage ? (
                            <LoadingAnimation />
                        ) : usageAssemblies.length > 0 ? (
                            <>
                                <p className='text-sm border-dashed border-2 border-red-500 bg-red-100 p-2 rounded-md mb-2'>
                                    <span className='text-red-600'>Warning:</span> This asset is attached to {usageAssemblies.length} assembl{usageAssemblies.length === 1 ? 'y' : 'ies'}. Deleting it will remove it from all of them.
                                </p>
                                <div className='max-h-40 overflow-y-auto text-sm mb-4'>
                                    {usageAssemblies.map((a) => (
                                        <p key={a._id} className='text-gray-600'>{a.assembly_number || a.model_number} — {a.assembly_description}</p>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className='text-sm text-gray-500 mb-4'>This asset isn't attached to any assembly.</p>
                        )}
                        <div className='flex flex-col md:flex-row-reverse gap-4'>
                            <ButtonSolid buttonText='Delete' disabled={deleting} showAnimation={deleting} onClick={confirmDelete} className='w-full' />
                            <ButtonOutline buttonText='Cancel' disabled={deleting} onClick={() => setAssetPendingDelete(null)} className='w-full' />
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default AssetManagerListView;
